<template>
  <section class="reserve-care-schedule mt-4" aria-label="智能個管師發送排程">
    <div class="d-flex align-center justify-space-between mb-2">
      <h3 class="text-subtitle-1 font-weight-bold">智能個管師訊息</h3>
      <v-btn v-if="!readOnly" small text color="primary" :disabled="loading || saving" @click="$emit('configure')">設定訊息</v-btn>
    </div>
    <div v-if="loading" class="d-flex align-center py-3" role="status">
      <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2"/>讀取發送排程中…
    </div>
    <v-alert v-else-if="listError" type="error" text dense role="alert">
      {{ listError }} <v-btn small text @click="fetchData">重新讀取</v-btn>
    </v-alert>
    <p v-else-if="!items.length" class="text-body-2 grey--text mb-0">{{ emptyMessage }}</p>
    <div v-for="(item, index) in items" v-else :key="item.group_uuid || index" class="care-group mb-2">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
        <h4 class="text-subtitle-2 font-weight-bold care-name">{{ item.tag_name }}</h4>
        <v-btn v-if="!readOnly && isEditable(item)" small text color="primary" :disabled="saving || loading" @click="openEditDialog(item)">
          編輯發送時間
        </v-btn>
      </div>
      <div v-for="(message, messageIndex) in item.messages" :key="message.id" class="care-row text-body-2">
        <span class="care-name">・{{ message.title || `細項 ${messageIndex + 1}` }}：</span>
        <time>{{ formatTime(message.scheduled_send_at) }}</time>
        <span :class="`${statusColor(message.send_status)}--text`">{{ formatStatus(message.send_status) }}</span>
      </div>
    </div>
    <care-schedule-edit-dialog :value="editDialog" :saving="saving" :current-item="currentItem"
      :editable-messages="editableMessages" :min-date="minDate"
      @toggle-edit="toggleEditMessage" @save="saveChanges" @close="closeEditDialog"/>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { getReserveCareSchedulesAPI } from '@/api/care';
import { normalizeReserveCareSchedules, smartCareDeliveryUnavailable } from '@/utils/smartCareReserve';
import careScheduleEditing from '@/mixins/careScheduleEditing';
import CareScheduleEditDialog from '@/components/PostOperativeCare/CareScheduleEditDialog.vue';

export default {
  name: 'ReserveCareSchedule',
  components: { CareScheduleEditDialog },
  mixins: [careScheduleEditing],
  props: {
    reserveNo: { type: [String, Number], required: true },
    patient: { type: Object, default: null },
    phoneVisible: Boolean,
    readOnly: Boolean,
    refreshKey: { type: Number, default: 0 },
  },
  data() { return { items: [], loading: false, listError: '', fetchRequestId: 0 }; },
  computed: {
    ...mapGetters(['axiosParamData']),
    emptyMessage() {
      return smartCareDeliveryUnavailable(this.patient, { ownerType: this.axiosParamData.type, phoneVisible: this.phoneVisible })
        ? '未綁定LINE無法發送' : '本次約診尚無智能個管師發送排程。';
    },
  },
  watch: {
    reserveNo() { this.closeEditDialog(); this.fetchData(); },
    refreshKey() { this.fetchData(); },
    axiosParamData: { deep: true, handler() { this.closeEditDialog(); this.fetchData(); } },
  },
  mounted() { this.fetchData(); },
  beforeDestroy() { this.fetchRequestId++; },
  methods: {
    formatTime(time) {
      const date = this.$moment(time);
      return time && date.isValid() ? date.tz('Asia/Taipei').format('YYYY-MM-DD HH:mm') : '時間未提供';
    },
    async fetchData() {
      const requestId = ++this.fetchRequestId;
      this.loading = true;
      this.items = [];
      this.listError = '';
      try {
        const reserveNo = this.reserveNo;
        const response = await getReserveCareSchedulesAPI(reserveNo, this.axiosParamData, () => requestId === this.fetchRequestId);
        if (requestId !== this.fetchRequestId) return;
        this.items = normalizeReserveCareSchedules(response, reserveNo);
      } catch (error) {
        if (requestId === this.fetchRequestId) this.listError = error.response && error.response.status === 403
          ? '目前無權限查詢此約診的發送排程。' : '無法讀取本次約診的發送排程，請重新讀取。';
      } finally {
        if (requestId === this.fetchRequestId) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.care-name { overflow-wrap: anywhere; min-width: 0; }
.care-row { display: flex; align-items: baseline; gap: 2px 6px; flex-wrap: wrap; padding: 2px 0; }
.care-group + .care-group { border-top: 1px solid #eee; padding-top: 8px; }
</style>
